import { useEffect, useState } from 'react';
import { fetchShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

interface LogRetentionConfig {
  system_logs_retention_days: number;
  audit_logs_retention_days: number;
}

interface LogRetentionStats {
  system_logs_retention_days: number;
  audit_logs_retention_days: number;
  total_system_logs: number;
  total_audit_logs: number;
  oldest_system_log?: string;
  newest_system_log?: string;
  oldest_audit_log?: string;
  newest_audit_log?: string;
}

interface User {
  id: string;
  email: string;
  full_name: string;
  email_verified: boolean;
}

export default function LogRetentionSettings() {
  const { showConfirm } = useDialog();
  const { t, i18n } = useTranslation('admin');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [settings, setSettings] = useState<LogRetentionConfig>({
    system_logs_retention_days: 90,
    audit_logs_retention_days: 365,
  });
  const [stats, setStats] = useState<LogRetentionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userError, setUserError] = useState(false);
  const [statsError, setStatsError] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        // Shared with useUser and the other compliance pages — one request per
        // page load instead of one per component.
        const data = await fetchShared<User>(SHARED_KEYS.profile, async () => {
          const response = await api.get<User>('/auth/profile');
          return response.data;
        });
        setUser(data);
      } catch {
        setUserError(true);
      }
    };
    fetchUser();
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setSettingsLoaded(false);
      const response = await api.get<LogRetentionConfig>('/settings/log-retention');
      setSettings(response.data);
      setSettingsLoaded(true);
      setMessage(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        return;
      }
      setMessage({ type: 'error', text: t('logRetention.errors.loadSettings') });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get<LogRetentionStats>('/settings/log-retention/stats');
      setStats(response.data);
      setStatsError(false);
    } catch {
      setStats(null);
      setStatsError(true);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settingsLoaded) return;
    setMessage(null);

    // Validate
    if (settings.system_logs_retention_days < 7 || settings.system_logs_retention_days > 3650) {
      setMessage({ type: 'error', text: t('logRetention.errors.systemRange') });
      return;
    }

    if (settings.audit_logs_retention_days < 30 || settings.audit_logs_retention_days > 3650) {
      setMessage({ type: 'error', text: t('logRetention.errors.auditRange') });
      return;
    }

    try {
      setSaving(true);
      await api.put('/settings/log-retention', settings);
      setMessage({ type: 'success', text: t('logRetention.messages.saved') });
      // Refresh stats
      await fetchStats();
    } catch {
      setMessage({ type: 'error', text: t('logRetention.errors.saveSettings') });
    } finally {
      setSaving(false);
    }
  };

  const handleCleanup = async () => {
    if (!settingsLoaded) return;
    const confirmed = await showConfirm(
      t('logRetention.cleanup.confirmTitle'),
      t('logRetention.cleanup.confirmDescription'),
      'warning'
    );
    if (!confirmed) return;

    try {
      setCleaning(true);
      const response = await api.post('/settings/log-retention/cleanup', {});
      setMessage({
        type: 'success',
        text: t('logRetention.messages.cleanupComplete', {
          system: response.data.system_logs_deleted,
          audit: response.data.audit_logs_deleted,
        }),
      });
      // Refresh stats
      await fetchStats();
    } catch {
      setMessage({ type: 'error', text: t('logRetention.errors.cleanup') });
    } finally {
      setCleaning(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t('logRetention.notAvailable');
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime())
      ? t('logRetention.notAvailable')
      : new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-light dark:bg-surface-dark flex items-center justify-center">
        <div className="text-center text-text-muted-light dark:text-text-muted-dark">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-text-muted-light dark:text-text-muted-dark">
            {t('logRetention.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6" role={userError ? 'alert' : 'status'}>
        <div className="rounded-2xl border border-border-strong-light dark:border-border-strong-dark bg-surface-card-light dark:bg-surface-card-dark p-6 text-text-primary-light dark:text-text-primary-dark">
          {userError ? t('logRetention.errors.loadProfile') : t('logRetention.loading')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark">
            {t('logRetention.title')}
          </h1>
          <p className="mt-2 max-w-3xl text-sm sm:text-base text-text-muted-light dark:text-text-muted-dark">
            {t('logRetention.description')}
          </p>
        </header>

        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            aria-live={message.type === 'error' ? 'assertive' : 'polite'}
            className={`mb-6 p-4 rounded-xl border ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6">
          {/* Settings Form */}
          <div className="bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark shadow-sm rounded-2xl p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-5">
              {t('logRetention.retentionPeriod')}
            </h2>
            {!settingsLoaded && (
              <button
                type="button"
                onClick={fetchSettings}
                className="min-h-11 mb-4 rounded-lg px-4 border border-current"
              >
                {t('common:actions.retry')}
              </button>
            )}
            <form onSubmit={handleSave}>
              <fieldset disabled={!settingsLoaded}>
                <div className="space-y-4">
                  {/* System Logs Retention */}
                  <div>
                    <label
                      htmlFor="system_logs_retention_days"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {t('logRetention.systemLogsRetention')}
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="number"
                        id="system_logs_retention_days"
                        min="7"
                        max="3650"
                        value={settings.system_logs_retention_days}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            system_logs_retention_days: parseInt(e.target.value) || 90,
                          })
                        }
                        className="app-field-control min-h-11 flex-1 min-w-0 block w-full px-3 rounded-none rounded-l-lg border-border-strong-light dark:border-border-strong-dark text-text-primary-light dark:text-text-primary-dark sm:text-sm"
                      />
                      <div className="inline-flex min-h-11 items-center px-3 rounded-r-lg border border-l-0 border-border-strong-light dark:border-border-strong-dark bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark text-sm">
                        {t('logRetention.days')}
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-text-muted-light dark:text-text-muted-dark">
                      {t('logRetention.systemLogsDescription')}
                    </p>
                  </div>

                  {/* Audit Logs Retention */}
                  <div>
                    <label
                      htmlFor="audit_logs_retention_days"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      {t('logRetention.auditLogsRetention')}
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="number"
                        id="audit_logs_retention_days"
                        min="30"
                        max="3650"
                        value={settings.audit_logs_retention_days}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            audit_logs_retention_days: parseInt(e.target.value) || 365,
                          })
                        }
                        className="app-field-control min-h-11 flex-1 min-w-0 block w-full px-3 rounded-none rounded-l-lg border-border-strong-light dark:border-border-strong-dark text-text-primary-light dark:text-text-primary-dark sm:text-sm"
                      />
                      <div className="inline-flex min-h-11 items-center px-3 rounded-r-lg border border-l-0 border-border-strong-light dark:border-border-strong-dark bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark text-sm">
                        {t('logRetention.days')}
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-text-muted-light dark:text-text-muted-dark">
                      {t('logRetention.auditLogsDescription')}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={saving || !settingsLoaded}
                    className="w-full min-h-11 flex justify-center items-center px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 dark:focus:ring-brand-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? t('logRetention.saving') : t('logRetention.save')}
                  </button>
                </div>
              </fieldset>
            </form>
          </div>

          {/* Statistics */}
          {stats && (
            <div className="bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark shadow-sm rounded-2xl p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5">
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('logRetention.statistics')}
                </h2>
                <button
                  onClick={handleCleanup}
                  disabled={cleaning || !settingsLoaded}
                  className="w-full sm:w-auto min-h-11 whitespace-nowrap px-4 border border-state-danger-light/20 dark:border-state-danger-dark/20 text-sm font-semibold rounded-xl text-state-danger-light dark:text-state-danger-dark bg-state-danger-bg-light dark:bg-state-danger-bg-dark hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-state-danger-light dark:focus:ring-state-danger-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cleaning ? t('logRetention.cleaning') : t('logRetention.cleanup.runNow')}
                </button>
              </div>

              <div className="space-y-6">
                {/* System Logs Stats */}
                <div>
                  <h3 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                    {t('logRetention.systemLogs')}
                  </h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.totalLogs')}
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
                        {stats.total_system_logs.toLocaleString(locale)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.retentionPeriod')}
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                        {t('logRetention.dayCount', { count: stats.system_logs_retention_days })}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.oldestLog')}
                      </dt>
                      <dd className="mt-1 text-xs text-slate-900 dark:text-white">
                        {formatDate(stats.oldest_system_log)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.newestLog')}
                      </dt>
                      <dd className="mt-1 text-xs text-slate-900 dark:text-white">
                        {formatDate(stats.newest_system_log)}
                      </dd>
                    </div>
                  </dl>
                </div>

                <hr />

                {/* Audit Logs Stats */}
                <div>
                  <h3 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                    {t('logRetention.auditLogs')}
                  </h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.totalLogs')}
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">
                        {stats.total_audit_logs.toLocaleString(locale)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.retentionPeriod')}
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-white">
                        {t('logRetention.dayCount', { count: stats.audit_logs_retention_days })}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.oldestLog')}
                      </dt>
                      <dd className="mt-1 text-xs text-slate-900 dark:text-white">
                        {formatDate(stats.oldest_audit_log)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('logRetention.newestLog')}
                      </dt>
                      <dd className="mt-1 text-xs text-slate-900 dark:text-white">
                        {formatDate(stats.newest_audit_log)}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div
                className="mt-6 p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl flex items-start gap-3"
                role="note"
              >
                <Info
                  className="mt-0.5 h-5 w-5 shrink-0 text-state-info-light dark:text-state-info-dark"
                  aria-hidden="true"
                />
                <p className="text-sm leading-relaxed text-state-info-light dark:text-state-info-dark">
                  {t('logRetention.cleanup.automaticNote')}
                </p>
              </div>
            </div>
          )}
          {!stats && statsError && (
            <div
              role="alert"
              className="rounded-2xl border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-5 text-state-danger-light dark:text-state-danger-dark"
            >
              {t('logRetention.errors.loadStats')}
              <button
                type="button"
                onClick={fetchStats}
                className="mt-3 flex min-h-11 items-center rounded-lg border border-current px-4"
              >
                {t('common:actions.retry')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
